/**
 * mobile-menu-css-predicate.js — the single drawer-CSS classifier
 * (Workstream B). Pure: token sets in, booleans out. The Node extraction
 * pass (css-tree) and the browser merge engine (CSSOM) both drive their
 * walks through THESE predicates, so parser and builder can never disagree
 * about what "drawer CSS" means.
 *
 * Classification rule, in precedence order, per individual selector:
 *   1. references `.cs-toggle` → HOST (the hamburger is host-owned, even in
 *      compound selectors like `#nav.cs-active .cs-toggle .cs-line`);
 *   2. references any drawer class (canonical set ∪ classes discovered
 *      inside the source nav's `.cs-ul-wrapper` subtree) → DRAWER;
 *   3. otherwise → HOST.
 *
 * A RULE whose selector list mixes both classifications is SPLIT, never
 * removed whole — `partitionSelectors` gives both halves.
 */

import { CANONICAL_DRAWER_CLASSES, ACTIVATION_CONTRACT } from './mobile-menu-contract.js';

const CLASS_TOKEN_RE = /\.(-?[_a-zA-Z][_a-zA-Z0-9-]*)/g;

/** All class tokens referenced by one selector string. */
export function classTokensInSelector(selector) {
  const tokens = new Set();
  const text = String(selector || '');
  let match;
  CLASS_TOKEN_RE.lastIndex = 0;
  while ((match = CLASS_TOKEN_RE.exec(text)) !== null) tokens.add(match[1]);
  return tokens;
}

/**
 * The drawer class-set for one source nav: the canonical CodeStitch drawer
 * classes plus every class actually present INSIDE its `.cs-ul-wrapper`
 * subtree — this is how `ww-menu-*` / `pb-menu-*` / `cs-menu-footer` chrome
 * is discovered without hardcoding site vocabularies. DOM-only (jsdom and
 * browser alike).
 */
export function collectDrawerClassSet(navRootEl) {
  const set = new Set(CANONICAL_DRAWER_CLASSES);
  const wrapper =
    navRootEl && typeof navRootEl.querySelector === 'function'
      ? navRootEl.querySelector('.cs-ul-wrapper')
      : null;
  if (!wrapper) return set;
  const all = [wrapper, ...wrapper.querySelectorAll('*')];
  for (const el of all) {
    const classList = el.classList;
    if (!classList) continue;
    for (const cls of classList) set.add(cls);
  }
  // The toggle is host-owned even if a source nav nests it oddly.
  set.delete(ACTIVATION_CONTRACT.toggleClass);
  // State classes never define ownership: an ACTIVE link inside the wrapper
  // would otherwise put `cs-active` in the drawer set, and every root-state
  // rule — including the source's `.cs-active:before` page BACKDROP — would
  // classify drawer and ride into the fragment, painting over the drawer on
  // foreign hosts (esoteric's all-black render; the phantom-bar hazard).
  // A state selector still classifies drawer when compounded with a real
  // drawer class (`.cs-active .cs-li`).
  set.delete(ACTIVATION_CONTRACT.rootActiveClass);
  set.delete(ACTIVATION_CONTRACT.bodyOpenClass);
  return set;
}

/**
 * Does one selector reference fragment-owned markup (MM-2)? Any selector
 * naming a `data-pb-mm-*` marker attribute was written by the merge engine
 * (the desktop-safety guard rule) or a fragment's own CSS — it is
 * fragment-owned in ANY media context, so re-apply/revert must strip it even
 * outside max-width blocks where the class predicate never looks.
 */
export function selectorTargetsFragmentMarkers(selector) {
  return String(selector || '').includes('data-pb-mm-');
}

/** Does one selector target the drawer, per the precedence rule above? */
export function selectorTargetsDrawer(selector, drawerClassSet) {
  const tokens = classTokensInSelector(selector);
  if (tokens.has(ACTIVATION_CONTRACT.toggleClass)) return false;
  for (const token of tokens) {
    if (drawerClassSet.has(token)) return true;
  }
  return false;
}

/**
 * Partition a rule's selector list. `selectors` is an array of selector
 * strings (the caller — css-tree or CSSOM — owns the parsing/serialization).
 */
export function partitionSelectors(selectors, drawerClassSet) {
  const drawer = [];
  const host = [];
  for (const selector of selectors || []) {
    (selectorTargetsDrawer(selector, drawerClassSet) ? drawer : host).push(selector);
  }
  return { drawer, host };
}

const MAX_WIDTH_RE = /max-width\s*:\s*([0-9.]+)(px|rem|em)/i;

/** Parse a media query's max-width into rem-normalized form, or null. */
export function parseMaxWidthEdge(mediaText) {
  const match = MAX_WIDTH_RE.exec(String(mediaText || ''));
  if (!match) return null;
  const value = parseFloat(match[1]);
  if (!Number.isFinite(value)) return null;
  const unit = match[2].toLowerCase();
  // px→rem at the CSS-standard 16px root; rem and em are equivalent in a
  // media query (both resolve against the initial font size).
  const rem = unit === 'px' ? value / 16 : value;
  return { raw: `${match[1]}${unit}`, rem };
}

/** Two media edges are the same breakpoint when their rem values match. */
export function sameEdge(a, b) {
  return !!a && !!b && Math.abs(a.rem - b.rem) < 0.001;
}

/**
 * Detect a nav's mobile edge from rule metadata:
 * `rules` is an array of { mediaText, selectors } for every rule inside a
 * max-width media block, where `toggleShown` marks a rule that SETS
 * `.cs-toggle`'s display to something visible (the caller derives it —
 * css-tree in the parser, CSSOM in the builder).
 *
 * The AUTHORITATIVE edge is the max-width whose block makes the hamburger
 * visible: verified on ww, the toggle is display:flex at 74.96875rem while a
 * WIDER 1299.5px block merely restyles it, and the shared link list appears
 * in desktop-adjustment blocks wider still — so neither "any structural
 * marker" nor "any drawer class" may pick the edge. Fallback (no display
 * rule found): largest max-width styling `.cs-ul-wrapper`/`.cs-toggle`.
 * Mixed units unify in rem (casablanca's 1023px === 63.9375rem).
 */
export function detectMobileEdge(rules) {
  let shown = null;
  let structural = null;
  for (const rule of rules || []) {
    const edge = parseMaxWidthEdge(rule.mediaText);
    if (!edge) continue;
    if (rule.toggleShown) {
      if (!shown || edge.rem > shown.rem) shown = edge;
      continue;
    }
    const relevant = (rule.selectors || []).some((sel) => {
      const tokens = classTokensInSelector(sel);
      return tokens.has('cs-ul-wrapper') || tokens.has(ACTIVATION_CONTRACT.toggleClass);
    });
    if (relevant && (!structural || edge.rem > structural.rem)) structural = edge;
  }
  return shown || structural;
}
